import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GenericUsageEventMetricsApi } from '../../services/api/GenericUsageEventMetricsApi';
import { GPTMetricsApi } from '../../services/ApiService';
import { PaymentEventsMetricsApi } from '../../services/api/PaymentEventsMetricsApi';
import { AnalyticsOverviewApi } from '../../services/api/AnalyticsOverviewApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  MetricCard, 
  InsightCard, 
  ActivityHeatmap,
  ExportButton 
} from '../../components/analytics/SharedAnalyticsComponents';

/**
 * Enhanced Analytics Overview - Executive Dashboard
 * Improvements:
 * - Sparkline trends in KPI cards with % change
 * - Top Insights section with auto-detected patterns
 * - Activity heatmap for time-of-day patterns
 * - System health indicators
 * - Quick actions for common tasks
 */
export default function AnalyticsOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 7 * 86400000); // Last 7 days
    return { startDate, endDate };
  });
  
  const [data, setData] = useState({
    usageEvents: { summary: null, volume: [] },
    gptPrompts: { summary: null, volume: [] },
    payments: { summary: null, volume: [] }
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correlationSearch, setCorrelationSearch] = useState('');
  const [aiOverview, setAiOverview] = useState(() => {
    try {
      const stored = localStorage.getItem('analytics_ai_overview');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiContext, setAiContext] = useState('');

  // Check if user is admin
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  useEffect(() => {
    if (!isAdmin) return;
    
    const loadAllMetrics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { startDate, endDate } = dateRange;
        
        // Fetch all metrics in parallel
        const [
          usageSummary,
          usageVolume,
          gptSummary,
          gptVolume,
          paymentSummary,
          paymentVolume
        ] = await Promise.all([
          GenericUsageEventMetricsApi.getSummaryStats(startDate, endDate).then(r => r.ok ? r.json() : null).catch(() => null),
          GenericUsageEventMetricsApi.getEventVolumeSeries(startDate, endDate).then(r => r.ok ? r.json() : []).catch(() => []),
          GPTMetricsApi.getSummary({ from: startDate, to: endDate }).then(r => r.ok ? r.json() : null).catch(() => null),
          GPTMetricsApi.getUsageSeries({ from: startDate, to: endDate, interval: 'day' }).then(r => r.ok ? r.json() : []).catch(() => []),
          PaymentEventsMetricsApi.getSummary({ from: startDate, to: endDate }).then(r => r.ok ? r.json() : null).catch(() => null),
          PaymentEventsMetricsApi.getVolumeSeries({ from: startDate, to: endDate }).then(r => r.ok ? r.json() : []).catch(() => [])
        ]);
        
        setData({
          usageEvents: { summary: usageSummary, volume: usageVolume },
          gptPrompts: { summary: gptSummary, volume: gptVolume },
          payments: { summary: paymentSummary, volume: paymentVolume }
        });
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllMetrics();
  }, [dateRange, isAdmin]);

  const handleQuickRange = (days) => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - days * 86400000);
    setDateRange({ startDate, endDate });
  };

  const handleCorrelationSearch = () => {
    if (correlationSearch.trim()) {
      window.open(`/settings/correlation/${correlationSearch.trim()}`, '_blank');
    }
  };

  const handleGenerateAiOverview = async () => {
    try {
      setAiLoading(true);
      setAiError(null);
      const days = Math.round((dateRange.endDate - dateRange.startDate) / 86400000) || 30;
      const result = await AnalyticsOverviewApi.getAdminOverview(days, aiContext.trim() || undefined);
      setAiOverview(result);
      try { localStorage.setItem('analytics_ai_overview', JSON.stringify(result)); } catch {}
    } catch (e) {
      console.error('AI overview generation failed:', e);
      setAiError(e.message || 'Failed to generate AI overview');
    } finally {
      setAiLoading(false);
    }
  };

  // Generate insights from data
  const insights = useMemo(() => {
    const results = [];
    
    // Cost warning
    if (data.gptPrompts.summary?.costUsd > 100) {
      results.push({
        type: 'warning',
        title: 'High AI Usage',
        message: `AI costs reached $${data.gptPrompts.summary.costUsd.toFixed(2)} this period. Consider setting budget alerts.`,
        action: { label: 'View AI Dashboard', link: '/settings/gpt-prompt-events' }
      });
    }
    
    // Payment failure rate
    if (data.payments.summary && data.payments.summary.totalEvents > 0) {
      const unprocessedCount = data.payments.summary.totalEvents - (data.payments.summary.processedEvents || 0);
      const failureRate = (unprocessedCount / data.payments.summary.totalEvents) * 100;
      if (failureRate > 5) {
        results.push({
          type: 'danger',
          title: 'Payment Issues',
          message: `${failureRate.toFixed(1)}% of payment events unprocessed. Check webhook health.`,
          action: { label: 'View Payments', link: '/settings/webhook-monitor' }
        });
      }
    }
    
    // Engagement insight
    if (data.usageEvents.summary && data.usageEvents.summary.uniqueUsers > 0) {
      const eventsPerUser = data.usageEvents.summary.totalEvents / data.usageEvents.summary.uniqueUsers;
      if (eventsPerUser > 50) {
        results.push({
          type: 'success',
          title: 'High Engagement',
          message: `Users averaging ${eventsPerUser.toFixed(0)} interactions each. Great activity!`,
          action: { label: 'View Details', link: '/settings/usage-events' }
        });
      }
    }
    
    return results;
  }, [data]);

  // Prepare sparkline data (last 7 days mini trend)
  const sparklineData = useMemo(() => ({
    ui: data.usageEvents.volume.slice(-7).map(d => ({ value: d.count || 0 })),
    ai: data.gptPrompts.volume.slice(-7).map(d => ({ value: d.prompts || 0 })),
    payments: data.payments.volume.slice(-7).map(d => ({ value: d.count || 0 }))
  }), [data]);

  // Merge volume data into unified timeline for chart
  const mergedVolumeData = useMemo(() => {
    // Normalize date strings to YYYY-MM-DD for consistent merging
    const normalizeDate = (d) => {
      if (!d) return null;
      return new Date(d).toISOString().split('T')[0];
    };

    // Collect all unique dates
    const dateSet = new Set();
    data.usageEvents.volume.forEach(d => { const nd = normalizeDate(d.date); if (nd) dateSet.add(nd); });
    data.gptPrompts.volume.forEach(d => { const nd = normalizeDate(d.bucket); if (nd) dateSet.add(nd); });
    data.payments.volume.forEach(d => { const nd = normalizeDate(d.date); if (nd) dateSet.add(nd); });
    
    const sortedDates = Array.from(dateSet).sort();
    
    // Create lookup maps with normalized keys
    const uiMap = new Map(data.usageEvents.volume.map(d => [normalizeDate(d.date), d.count]));
    const aiMap = new Map(data.gptPrompts.volume.map(d => [normalizeDate(d.bucket), d.prompts]));
    const paymentMap = new Map(data.payments.volume.map(d => [normalizeDate(d.date), d.count]));
    
    // Merge into single array
    return sortedDates.map(date => ({
      date,
      uiEvents: uiMap.get(date) || 0,
      aiPrompts: aiMap.get(date) || 0,
      payments: paymentMap.get(date) || 0
    }));
  }, [data]);

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Analytics Overview</h2>
        <div className="empty-state">Admin access required to view analytics overview.</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem', marginBottom:'1.5rem' }}>
        <h2 style={{ margin:0 }}>Analytics Overview</h2>
        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
          <button className="secondary" onClick={() => handleQuickRange(1)}>24h</button>
          <button className="secondary" onClick={() => handleQuickRange(7)}>7d</button>
          <button className="secondary" onClick={() => handleQuickRange(30)}>30d</button>
          <button className="secondary" onClick={() => handleQuickRange(90)}>90d</button>
          <ExportButton 
            data={{
              summary: {
                ui: data.usageEvents.summary,
                ai: data.gptPrompts.summary,
                payments: data.payments.summary
              },
              volume: {
                ui: data.usageEvents.volume,
                ai: data.gptPrompts.volume,
                payments: data.payments.volume
              },
              insights,
              dateRange
            }}
            filename="analytics-overview"
          />
        </div>
      </div>

      {/* Correlation ID Search */}
      <div style={{ marginBottom:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
        <h3 style={{ marginTop:0, fontSize:'.95rem', marginBottom:'0.5rem' }}>🔍 Search by Correlation ID</h3>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <input 
            type="text" 
            placeholder="Enter correlation ID (UUID)" 
            value={correlationSearch}
            onChange={(e) => setCorrelationSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCorrelationSearch()}
            style={{ flex:1 }}
          />
          <button onClick={handleCorrelationSearch} disabled={!correlationSearch.trim()}>
            Search
          </button>
        </div>
        <div style={{ marginTop:'0.5rem', fontSize:'.7rem', color:'var(--text-muted)' }}>
          Track events across all services using a correlation ID
        </div>
      </div>

      {loading && <div>Loading analytics...</div>}
      {error && <div style={{ color:'var(--color-danger, #c00)' }}>Error loading analytics: {error.message}</div>}

      {!loading && (
        <>
          {/* Insights */}
          {insights.length > 0 && (
            <div style={{ marginBottom:'1.5rem' }}>
              {insights.map((insight, idx) => (
                <InsightCard key={idx} {...insight} />
              ))}
            </div>
          )}

          {/* AI-Generated Overview */}
          <div style={{ marginBottom:'1.5rem', background:'var(--card-bg)', padding:'1.25rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: aiOverview ? '0.5rem' : 0 }}>
              <h3 style={{ margin:0, fontSize:'.95rem' }}>🤖 AI Analytics Overview</h3>
              <button
                onClick={handleGenerateAiOverview}
                disabled={aiLoading}
                style={{ opacity: aiLoading ? 0.6 : 1 }}
              >
                {aiLoading ? 'Generating...' : aiOverview ? 'Regenerate' : 'Generate Overview'}
              </button>
            </div>
            {aiOverview?.generated_at && (
              <div style={{ marginBottom:'0.75rem', fontSize:'.75rem', color:'var(--text-muted)' }}>
                Last run: {new Date(aiOverview.generated_at).toLocaleString()}{aiOverview.data_range?.days ? ` · ${aiOverview.data_range.days}-day window` : ''}
              </div>
            )}
            <textarea
              value={aiContext}
              onChange={e => setAiContext(e.target.value)}
              placeholder="Optional: tell the AI what you'd like feedback on (e.g. 'Focus on churn trends and cost optimization' or 'Why are quiz scores declining?')"
              rows={2}
              style={{ width:'100%', marginBottom:'0.75rem', padding:'0.5rem 0.75rem', fontSize:'.8rem', borderRadius:'8px', border:'1px solid var(--border-light, #d1d5db)', background:'var(--input-bg, #fff)', color:'var(--text-primary)', resize:'vertical', fontFamily:'inherit' }}
            />
            {aiError && (
              <div style={{ marginTop:'0.75rem', padding:'0.5rem 0.75rem', background:'var(--color-danger-bg, #fef2f2)', color:'var(--color-danger, #991b1b)', borderRadius:'8px', fontSize:'.8rem' }}>
                {aiError}
              </div>
            )}
            {aiOverview && (
              <div>
                <div style={{ fontSize:'.85rem', lineHeight:'1.6', color:'var(--text-primary)', whiteSpace:'pre-wrap' }}>
                  {aiOverview.overview}
                </div>
                {aiOverview.suggestions?.length > 0 && (
                  <div style={{ marginTop:'1rem' }}>
                    <h4 style={{ margin:'0 0 0.5rem', fontSize:'.85rem', fontWeight:600 }}>Suggestions</h4>
                    <ul style={{ margin:0, paddingLeft:'1.25rem', fontSize:'.8rem', lineHeight:'1.7', color:'var(--text-secondary)' }}>
                      {aiOverview.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}

              </div>
            )}
            {!aiOverview && !aiLoading && !aiError && (
              <div style={{ marginTop:'0.5rem', fontSize:'.75rem', color:'var(--text-muted)' }}>
                Runs aggregation jobs, collects all analytics data, and generates an AI-powered overview with suggestions.
              </div>
            )}
          </div>

          {/* Enhanced KPI Cards with Sparklines */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:'1rem', marginBottom:'1.5rem' }}>
            <MetricCard 
              label="UI Events" 
              value={data.usageEvents.summary?.totalEvents?.toLocaleString() || '0'}
              subtitle={`${data.usageEvents.summary?.uniqueUsers || 0} users`}
              trend={sparklineData.ui.length > 1 ? ((sparklineData.ui[sparklineData.ui.length-1]?.value - sparklineData.ui[0]?.value) / (sparklineData.ui[0]?.value || 1) * 100) : 0}
              sparklineData={sparklineData.ui}
              color="#007bff"
              onClick={() => navigate('/settings/usage-events')}
            />
            <MetricCard 
              label="AI Prompts" 
              value={data.gptPrompts.summary?.prompts?.toLocaleString() || '0'}
              subtitle={`$${(data.gptPrompts.summary?.costUsd || 0).toFixed(2)} cost`}
              trend={sparklineData.ai.length > 1 ? ((sparklineData.ai[sparklineData.ai.length-1]?.value - sparklineData.ai[0]?.value) / (sparklineData.ai[0]?.value || 1) * 100) : 0}
              sparklineData={sparklineData.ai}
              color="#28a745"
              onClick={() => navigate('/settings/gpt-prompt-events')}
            />
            <MetricCard 
              label="Payment Events" 
              value={data.payments.summary?.totalEvents?.toLocaleString() || '0'}
              subtitle={data.payments.summary?.totalEvents > 0 ? `${((data.payments.summary?.processedEvents / data.payments.summary.totalEvents) * 100).toFixed(1)}% processed` : 'No events'}
              trend={sparklineData.payments.length > 1 ? ((sparklineData.payments[sparklineData.payments.length-1]?.value - sparklineData.payments[0]?.value) / (sparklineData.payments[0]?.value || 1) * 100) : 0}
              sparklineData={sparklineData.payments}
              color="#ffc107"
              onClick={() => navigate('/settings/webhook-monitor')}
            />
            <MetricCard 
              label="Active Sessions" 
              value={data.usageEvents.summary?.uniqueSessions?.toLocaleString() || '0'}
              subtitle={`${data.usageEvents.summary?.uniqueEventTypes || 0} event types`}
              color="#17a2b8"
              onClick={() => navigate('/settings/usage-events')}
            />
          </div>

          {/* Combined Volume Chart */}
          <div style={{ marginBottom:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
            <h3 style={{ marginTop:0, fontSize:'.95rem' }}>Event Volume Trends</h3>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={mergedVolumeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleString()} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="uiEvents" 
                  name="UI Events" 
                  stroke="#007bff" 
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey="aiPrompts" 
                  name="AI Prompts" 
                  stroke="#28a745" 
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey="payments" 
                  name="Payments" 
                  stroke="#ffc107" 
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Activity Heatmap */}
          {data.usageEvents.volume.length > 0 && (
            <div style={{ marginBottom:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0, fontSize:'.95rem', marginBottom:'0.75rem' }}>Activity Heatmap (Last 7 Days)</h3>
              <ActivityHeatmap 
                data={data.usageEvents.volume.map(d => ({
                  date: d.date,
                  value: d.count
                }))}
                title="UI Event Activity"
              />
            </div>
          )}

          {/* Quick Links to Detailed Dashboards */}
          <div style={{ marginBottom:'1.5rem' }}>
            <h3 style={{ fontSize:'.95rem', marginBottom:'0.75rem' }}>Detailed Analytics</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'1rem' }}>
              <DashboardLinkCard 
                title="Funnel Builder"
                description="Visualize multi-step conversion funnels and drop-offs"
                link="/analytics/funnel-builder"
                icon="🔀"
              />
              <DashboardLinkCard 
                title="Cohort Retention"
                description="Analyze user retention rates with heatmap visualization"
                link="/analytics/retention"
                icon="📈"
              />
              <DashboardLinkCard 
                title="User Acquisition"
                description="Track traffic sources, UTM campaigns, and conversion rates"
                link="/analytics/acquisition"
                icon="🎯"
              />
              <DashboardLinkCard 
                title="Usage Events Dashboard"
                description="Track UI interactions, clicks, and user behavior"
                link="/settings/usage-events"
                icon="📊"
              />
              <DashboardLinkCard 
                title="GPT Prompt Analytics"
                description="Monitor AI usage, costs, and token consumption"
                link="/settings/gpt-prompt-events"
                icon="🤖"
              />
              <DashboardLinkCard 
                title="Webhook Monitor"
                description="Payment event processing and webhook health"
                link="/settings/webhook-monitor"
                icon="💳"
              />
              <DashboardLinkCard 
                title="Payments Dashboard"
                description="Transaction history and payment status"
                link="/settings/payments-dashboard"
                icon="💰"
              />
              <DashboardLinkCard 
                title="Data Retention"
                description="Manage event retention and cleanup policies"
                link="/settings/data-retention"
                icon="🗄️"
              />
              <DashboardLinkCard 
                title="Learning Intelligence"
                description="Document engagement, study funnels, feature adoption, sessions & user profiles"
                link="/analytics/learning-intelligence"
                icon="🧠"
              />
              <DashboardLinkCard 
                title="Analytics Toolkit"
                description="Funnel builder with save/load, cohort retention matrix, journey pattern explorer"
                link="/analytics/toolkit"
                icon="🧰"
              />
              <DashboardLinkCard 
                title="Conversion Goals"
                description="Define, track, and analyze multi-step conversion funnels with target rates"
                link="/analytics/conversion-goals"
                icon="🎯"
              />
              <DashboardLinkCard 
                title="Alert Rules"
                description="Configure automated alerts for cost thresholds, latency spikes, and engagement anomalies"
                link="/settings/alerts"
                icon="🔔"
              />
              <DashboardLinkCard 
                title="ML Insights"
                description="Churn prediction, user segmentation, cost forecasting & anomaly detection powered by ML"
                link="/analytics/ml-insights"
                icon="🤖"
              />
              <DashboardLinkCard 
                title="Error Investigation"
                description="Investigate client-side errors, stack traces, browser stats, and error trends"
                link="/analytics/errors"
                icon="🐛"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DashboardLinkCard({ title, description, link, icon }) {
  return (
    <Link 
      to={link}
      style={{
        textDecoration:'none',
        color:'inherit',
        background:'var(--card-bg)',
        border:'1px solid var(--border-light)',
        borderRadius:'12px',
        padding:'1rem',
        boxShadow:'var(--shadow-sm)',
        transition:'all 0.2s',
        display:'flex',
        gap:'0.75rem',
        alignItems:'flex-start'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ fontSize:'2rem' }}>{icon}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:'.9rem', fontWeight:600, marginBottom:'0.25rem' }}>{title}</div>
        <div style={{ fontSize:'.75rem', color:'var(--text-secondary)' }}>{description}</div>
      </div>
      <div style={{ fontSize:'1.5rem', color:'var(--text-muted)' }}>→</div>
    </Link>
  );
}
